import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { AuthService } from '../services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (verification_id: string) => Promise<void>;
  logout: () => Promise<void>;
  requestVerification: (email: string) => Promise<string>;
  verifyEmail: (email: string, code: string) => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await AuthService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, []);

  const login = async (verification_id: string) => {
    setIsLoading(true);
    try {
      const userData = await AuthService.register(verification_id);
      setUser(userData);
      setIsAuthenticated(true);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await AuthService.logout();
      setUser(null);
      setIsAuthenticated(false);
      // Use navigate instead of window.location for a smoother transition
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear the state even if the API call fails
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/';
    }
  };

  const requestVerification = async (email: string): Promise<string> => {
    const response = await AuthService.requestVerification(email);
    return response.verification_id;
  };

  const verifyEmail = async (email: string, code: string): Promise<string> => {
    const response = await AuthService.verifyEmail(email, code);
    return response.verification_id;
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    requestVerification,
    verifyEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 